/* L2 iBrushy cart (contract §5.2, §5.2.1): window.ibCart on native /cart/*.js, the cart drawer (sections/ib-cart-drawer.liquid)
   and the /cart page (sections/ib-cart-page.liquid). Rendered state is server-side only: every op asks the Section Rendering API
   for the mounted [data-ib-role] instances and swaps #shopify-section-<id> (skipped when its data-ib-cart-sig is unchanged).
   Foreign cart writes (fetch), the bag click, bfcache restores and tab returns re-sync through refresh().
   No discount, gift or protection logic here. */
(() => {
  'use strict';
  if (window.ibCart) return;

  // Transport: the native fetch captured at parse time, so a later fetch wrapper (ib-cart-shortly.js) never sees our requests.
  const nativeFetch = window.fetch.bind(window);
  // Live parity (measured on the live theme, 07/10): fetch is patched and the bag follows any cart write made by another
  // script in the page (apps, L1 ATC). Every successful foreign POST to cart/add|change|update|clear re-syncs the roles.
  // ponytail: XMLHttpRequest writes are not watched (live does not sync them either); visibilitychange covers the rest.
  const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?([?#]|$)/;
  const CART_ADD = /\/cart\/add(\.js)?([?#]|$)/;
  let writes = 0; // foreign cart writes seen so far
  let synced = -1; // `writes` as of the last server render of the roles (own op sections or refresh); -1 = page HTML only
  window.fetch = function (input, init) {
    const url = String((input && input.url) || input);
    const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    // 16/10: a foreign JSON add (ib-pdp.js on the Instant PDPs, via the kit guard) asks for the roles' sections too, so the
    // drawer renders from its answer instead of a second GET (~0.4 s). Other bodies (FormData, sections already asked) go as is.
    let asked = false;
    if (method === 'POST' && CART_ADD.test(url) && !(input && input.url) && init && typeof init.body === 'string') {
      try {
        const b = JSON.parse(init.body);
        const ids = b && typeof b === 'object' && !Array.isArray(b) && b.sections == null ? roleIds() : [];
        if (ids.length) {
          init = { ...init, body: JSON.stringify({ ...b, sections: ids.join(','), sections_url: location.pathname }) };
          asked = true;
        }
      } catch (e) { /* not JSON: as is */ }
    }
    const res = nativeFetch(input, init);
    if (method === 'POST' && CART_WRITE.test(url)) {
      res.then((r) => {
        if (r.ok) {
          writes += 1;
          let answer = null;
          try { answer = asked ? r.clone() : null; } catch (e) { /* body already read: GET */ }
          refresh(answer);
        }
      }, () => {});
    }
    return res;
  };
  const ROLES = ['header', 'drawer', 'cart-page'];
  const PATHS = {
    add: 'cart/add.js', change: 'cart/change.js', update_lines: 'cart/update.js',
    update_attributes: 'cart/update.js', clear: 'cart/clear.js', get: 'cart.js'
  };
  const WITH_SECTIONS = { add: true, change: true, update_lines: true, clear: true };

  const root = () => {
    const r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.endsWith('/') ? r : r + '/';
  };
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));
  const fail = (op, status, message) => ({ ok: false, op, status, message: String(message || '') });
  const opName = (op) => (op && (op.op || op.type)) || '';
  const opInput = (op) => (op && op.input) || op || {};

  // Section ids of the mounted roles, read at request time (never cached, never a type name).
  function roleIds() {
    const ids = [];
    for (const role of ROLES) {
      const els = document.querySelectorAll('[data-ib-role="' + role + '"]');
      if (els.length === 1 && els[0].dataset.sectionId) ids.push(els[0].dataset.sectionId);
      else if (role !== 'cart-page' || els.length) {
        emit('ib:cart:error', { op: 'sections', status: null, message: 'role ' + role + ': ' + els.length + ' mounted' });
      }
    }
    return ids;
  }

  async function getSections(ids) {
    const r = await nativeFetch(location.pathname + '?sections=' + encodeURIComponent(ids.join(',')), { credentials: 'same-origin' });
    if (!r.ok) throw new Error('sections ' + r.status);
    return r.json();
  }

  // One request for one op. Resolves {ok, op, data, sections, response}, rejects {ok: false, op, status, message}.
  async function request(op) {
    const type = opName(op);
    const input = opInput(op);
    const path = PATHS[type];
    if (!path) {
      const err = fail(type, 0, 'unknown op');
      emit('ib:cart:error', { op: type, status: 0, message: err.message });
      throw err;
    }
    let body = null;
    let ids = null;
    if (type !== 'get') {
      if (type === 'add') body = { items: input.items };
      else if (type === 'change') {
        body = { quantity: input.quantity };
        if (input.key != null || input.id != null) body.id = input.key != null ? input.key : input.id;
        else body.line = input.line;
      } else if (type === 'update_lines') body = { updates: input.updates };
      else if (type === 'update_attributes') body = { attributes: input.attributes };
      else body = {};
      if (WITH_SECTIONS[type]) {
        ids = Array.isArray(input.sections) ? input.sections : roleIds();
        if (ids.length) {
          body.sections = ids.join(',');
          body.sections_url = location.pathname;
        }
      }
    }
    const init = body
      ? { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }
      : { method: 'GET', credentials: 'same-origin', headers: { Accept: 'application/json' } };

    let res;
    const seen = writes;
    try {
      res = await nativeFetch(root() + path, init);
    } catch (e) {
      const err = fail(type, 0, (e && e.message) || 'network error');
      emit('ib:cart:error', { op: type, status: 0, message: err.message });
      throw err;
    }
    const response = res.clone();
    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }
    if (!res.ok || data === null) {
      // the server answered with an error (status) or an unreadable body (outcome unknown = 0)
      const status = res.ok ? 0 : res.status;
      const err = fail(type, status, (data && (data.description || data.message)) || res.statusText || 'bad response');
      emit('ib:cart:error', { op: type, status, message: err.message });
      throw err;
    }

    let sections = null;
    if (ids && ids.length) {
      sections = data.sections || null;
      if (type === 'clear' && !sections) {
        // U38: clear.js with sections is unverified; one GET in this op's own slot.
        try {
          sections = await getSections(ids);
        } catch (e) {
          sections = null;
          emit('ib:cart:error', { op: 'sections', status: null, message: 'clear fallback: ' + e.message });
        }
      }
      if (sections) {
        await render(sections, ids);
        synced = seen;
      } else if (type !== 'clear') emit('ib:cart:error', { op: 'sections', status: null, message: 'no sections in ' + type });
    }
    const result = { ok: true, op: type, data, sections, response };
    emit('ib:cart:updated', { op: type, data, sections });
    // the badge counts what the drawer shows: hidden bags (_instant-hidden) stay out, as in ib-header.liquid
    if (Array.isArray(data.items) && typeof data.item_count === 'number') {
      emit('ib:cart:count', { count: data.items.reduce((s, it) => s + (String((it.properties || {})['_instant-hidden']) === 'true' ? 0 : it.quantity), 0) });
    }
    return result;
  }

  // One serial FIFO queue. slot(fn) runs fn alone; the next slot starts when fn's promise settles.
  let tail = Promise.resolve();
  function slot(fn) {
    const run = tail.then(fn);
    tail = run.catch(() => {});
    return run;
  }

  const enqueue = (op) => slot(() => request(op));

  function batch(ops, opts) {
    const dependent = !!(opts && opts.dependent);
    return slot(async () => {
      const out = [];
      let broken = false;
      for (const op of ops) {
        if (broken) {
          out.push({ ok: false, op: opName(op), skipped: true, status: null });
          continue;
        }
        try {
          out.push(await request(op));
        } catch (e) {
          out.push(e);
          if (dependent) broken = true;
        }
      }
      return out;
    });
  }

  // The callback holds the queue: tx.get / tx.send each send exactly one request now; foreign ops wait (FIFO).
  function transaction(cb) {
    return slot(() => cb({ get: () => request({ op: 'get' }), send: (op) => request(op) }));
  }

  // Executor of cream ops (contract cart.ibcart_api.transaction). The ops and the repairs come from L1 (window.ibOffers);
  // gift adds are flagged op.gift === true by the planner. Every gift add.js sent spends the run budget (2).
  function runCream(ops, opts) {
    const nonce = opts && opts.atc_nonce;
    const isGift = (op) => opName(op) === 'add' && op.gift === true;
    return transaction(async (tx) => {
      let addsLeft = 2;
      let atcUnknown = false;
      const sendGift = async (op) => {
        if (addsLeft <= 0) throw fail('add', null, 'gift add budget spent');
        addsLeft -= 1;
        return tx.send(op);
      };
      // (2)-(4) send the ops in order
      for (const op of ops || []) {
        try {
          await (isGift(op) ? sendGift(op) : tx.send(op));
          continue;
        } catch (e) {
          if (e.status && isGift(op) && addsLeft > 0) {
            try {
              await sendGift(op);
              continue;
            } catch (e2) { /* fall through to reconcile */ }
          }
          if (e.status === 0 && nonce && opName(op) === 'add') atcUnknown = true;
          break;
        }
      }
      // (5)-(7) reconcile, at most two passes
      const plan = window.ibOffers && window.ibOffers.planReconcile;
      for (let pass = 0; pass < 2; pass += 1) {
        let cart;
        try {
          cart = (await tx.get()).data;
        } catch (e) {
          setCheckoutDisabled(true);
          return { ok: false, error: e };
        }
        if (atcUnknown) {
          // (8) the cart decides: a line carrying the nonce = the add happened; never re-sent
          const landed = (cart.items || []).some((it) => JSON.stringify(it.properties || {}).indexOf(nonce) !== -1);
          if (!landed) return { ok: false, error: fail('add', 0, 'atc outcome: not in cart') };
          atcUnknown = false;
          showError(false);
        }
        const fix = plan ? plan(cart, { adds_left: addsLeft }) || [] : [];
        let ok = true;
        for (const op of fix) {
          try {
            await (isGift(op) ? sendGift(op) : tx.send(op));
          } catch (e) {
            ok = false;
            break;
          }
        }
        if (ok) {
          setCheckoutDisabled(false);
          return { ok: true };
        }
      }
      showError(true);
      return { ok: false, error: fail('reconcile', null, 'second reconcile pass failed') };
    });
  }

  // ---------- section rendering ----------
  const sig = (el) => {
    const s = el.querySelector('[data-ib-cart-sig]');
    return s ? s.getAttribute('data-ib-cart-sig') : null;
  };

  const still = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const byKey = (box, key) => [...box.querySelectorAll('[data-ib-line-key]')].find((n) => n.getAttribute('data-ib-line-key') === key);
  // the control a line keeps across a swap (stepper -, input, stepper +, trash)
  const ctl = (el) => ['[data-ib-qty="-1"]', '[data-ib-qty="1"]', '[data-ib-qty-input]', '[data-ib-remove]'].find((s) => el.matches(s));

  // open drawer: rows gone from the fresh render collapse (height + opacity, 300 ms) before the swap
  function collapse(target, fresh) {
    if (still() || !target.querySelector('[data-ib-cart-overlay][data-state="open"]')) return null;
    const keep = new Set([...fresh.querySelectorAll('[data-ib-line-key]')].map((n) => n.getAttribute('data-ib-line-key')));
    const gone = [...target.querySelectorAll('[data-ib-line-key]')].filter((n) => !keep.has(n.getAttribute('data-ib-line-key')));
    if (!gone.length || !Element.prototype.animate) return null;
    return Promise.all(gone.map((n) => n.animate(
      [{ height: n.offsetHeight + 'px', minHeight: '0px', opacity: 1 }, { height: '0px', minHeight: '0px', opacity: 0, borderBottomWidth: '0px' }],
      { duration: 300, easing: 'ease', fill: 'forwards' }
    ).finished.catch(() => {})));
  }

  // the recs list fades its bottom edge until it is scrolled to the end
  const recEnd = (g) => g.toggleAttribute('data-end', g.scrollHeight - g.scrollTop - g.clientHeight <= 1);
  const recEnds = () => document.querySelectorAll('[data-ib-recs]').forEach(recEnd);

  async function render(sections, ids) {
    for (const id of ids) {
      if (!(id in sections) || !sections[id]) emit('ib:cart:error', { op: 'sections', status: null, message: 'missing section ' + id });
    }
    const done = [];
    for (const id of Object.keys(sections)) {
      if (ids.indexOf(id) === -1) {
        emit('ib:cart:error', { op: 'sections', status: null, message: 'unrequested section ' + id });
        continue;
      }
      const target = document.getElementById('shopify-section-' + id);
      if (!target || !sections[id]) continue;
      const doc = new DOMParser().parseFromString(sections[id], 'text/html');
      const fresh = doc.getElementById('shopify-section-' + id) || doc.body;
      // same cart as on screen: keep the DOM (no restart of the lazy images)
      const was = sig(target);
      if (was !== null && was === sig(fresh)) continue;
      const anim = collapse(target, fresh);
      if (anim) await anim;
      const active = document.activeElement;
      const focused = target.contains(active);
      const row = focused && active.closest('[data-ib-line-key]');
      const back = row && ctl(active) ? [row.getAttribute('data-ib-line-key'), ctl(active)] : null;
      const same = (el) => (el.id ? fresh.querySelector('#' + CSS.escape(el.id)) : null);
      // keep what the user has open or typed
      target.querySelectorAll('[data-state="open"][id]').forEach((el) => {
        const n = same(el);
        if (n) n.setAttribute('data-state', 'open');
      });
      target.querySelectorAll('textarea[id]').forEach((el) => {
        const n = same(el);
        if (n) n.textContent = el.value;
      });
      const scrolls = {};
      target.querySelectorAll('[data-ib-cart-box="body"],[data-ib-cart-box="recs"]').forEach((el) => {
        scrolls[el.getAttribute('data-ib-cart-box')] = el.scrollTop;
      });
      const hasBadge = !!target.querySelector('[data-ib-cart-count]');
      if (hasBadge) hold();
      target.replaceChildren(...[...fresh.childNodes].map((n) => document.importNode(n, true)));
      const nb = hasBadge && target.querySelector('[data-ib-cart-count]');
      if (nb) badge([nb.textContent, nb.style.display]);
      for (const box of Object.keys(scrolls)) {
        const el = target.querySelector('[data-ib-cart-box="' + box + '"]');
        if (el && scrolls[box]) el.scrollTop = scrolls[box];
      }
      if (focused) {
        // focus stays on the same control of the same line, else on the panel
        const line = back && byKey(target, back[0]);
        const to = line && line.querySelector(back[1]);
        const panel = target.querySelector('[data-state="open"] [data-ib-cart-box="panel"]');
        if (to && !to.disabled) to.focus({ preventScroll: true });
        else if (panel) panel.focus({ preventScroll: true });
      }
      done.push(id);
    }
    if (done.length) {
      recEnds();
      mark();
      emit('ib:sections:rendered', { ids: done });
    }
  }

  // Re-render the mounted roles from the server cart (a cart changed by another script, another tab, the bfcache).
  // A pending refresh is reused unless a foreign write landed after its GET started.
  // answer: the Response of a foreign add that asked for the sections (fetch wrapper); rendered instead of the GET.
  let pending = null;
  function refresh(answer) {
    if (!answer && pending && (pending.seen === null || pending.seen === writes)) return pending.run;
    // an answer is as of its own write: a later write never reuses it
    const p = { seen: answer ? writes : null };
    p.run = slot(async () => {
      if (!answer) p.seen = writes;
      const ids = roleIds();
      if (!ids.length) return;
      try {
        const s = answer && (await answer.json().catch(() => null) || {}).sections;
        await render(s && ids.every((id) => s[id]) ? s : await getSections(ids), ids);
        synced = p.seen;
      } catch (e) {
        emit('ib:cart:error', { op: 'sections', status: null, message: 'refresh: ' + e.message });
      }
    });
    pending = p;
    const done = () => { if (pending === p) pending = null; };
    p.run.then(done, done);
    return p.run;
  }

  // ---------- drawer ----------
  const overlay = () => document.querySelector('[data-ib-cart-overlay]');
  let lastFocus = null;
  // /cart is not a page (Simone 09/10): links to it open the drawer; layout/ib-cart-redirect.liquid sends a /cart visit
  // back to its page with sessionStorage 'ib-cart-open' (or #cart). /cart/<id>:1 permalinks do not match.
  const CART_PAGE = /^(\/[a-z]{2}(-[a-z]{2})?)?\/cart\/?$/i;
  const cartLink = (a) => a.host === location.host && CART_PAGE.test(a.pathname);

  function open() {
    const o = overlay();
    if (!o) return; // no drawer on this page; never navigate to /cart (it redirects back here)
    if (o.getAttribute('data-state') === 'open') return;
    lastFocus = document.activeElement;
    o.setAttribute('data-state', 'open');
    document.documentElement.classList.add('ib-cart-lock');
    const panel = o.querySelector('[data-ib-cart-box="panel"]');
    if (panel) panel.focus({ preventScroll: true });
    recEnds();
  }

  function close() {
    const o = overlay();
    if (!o || o.getAttribute('data-state') !== 'open') return;
    o.setAttribute('data-state', 'closed');
    document.documentElement.classList.remove('ib-cart-lock');
    // a render swapped the header: the old bag is gone, go back to the live (visible) one
    let to = lastFocus;
    if (to && !to.isConnected) to = [...document.querySelectorAll('[data-ib-cart-open]')].find((x) => x.offsetWidth) || null;
    if (to && to.focus) to.focus({ preventScroll: true });
    lastFocus = null;
  }

  function showError(on) {
    document.querySelectorAll('[data-ib-cart-zone="error"]').forEach((z) => { z.hidden = !on; });
  }

  function setCheckoutDisabled(on) {
    document.querySelectorAll('[data-ib-checkout]').forEach((el) => {
      if (on) el.setAttribute('aria-disabled', 'true');
      else el.removeAttribute('aria-disabled');
    });
  }

  const quiet = (p) => p.catch(() => {}); // failures already surface through ib:cart:error

  // ponytail: every line goes through change {key, quantity}; cream lines route through
  // ibCart.runCream(ibOffers.planLineMutation(...)) once L1 ships the planner (gift logic = backend phase).
  const setLine = (input) => quiet(enqueue({ op: 'change', input }));
  // drawer rows are not optimistic: the row shows its loader until the cart is idle again, i.e. the change has landed
  // AND the kit guard's reconcile after it is done. Meanwhile every qty/remove tap is ignored: a tap computed from the
  // in-between row (before the guard split the kit) added one paid unit too many (VERIFY-H F1).
  let busy = 0;
  const loading = new Set();
  const mark = () => {
    document.querySelectorAll('[data-ib-line-key]').forEach((n) => {
      n.classList.toggle('is-loading', loading.has(n.getAttribute('data-ib-line-key')));
    });
    // R14: the whole list says it is busy (taps ignored) until the cart is idle again; ib-cart.css dims its controls
    document.querySelectorAll('[data-ib-cart-zone="lines"]').forEach((z) => {
      if (busy) z.setAttribute('aria-busy', 'true');
      else z.removeAttribute('aria-busy');
    });
    // R13: the checkout waits too, so it never leaves with an in-between cart or a stale total (ib-cart.css dims it).
    // Its own attribute: aria-disabled belongs to the kit guard.
    document.querySelectorAll('[data-ib-checkout]').forEach((el) => el.toggleAttribute('data-ib-busy', !!busy));
  };
  // idle = no slot queued after ours for 250 ms and the guard (Web Lock 'ib2-kit-guard') neither holds nor waits for it
  async function settled() {
    for (let i = 0; i < 40; i += 1) { // ponytail: cap (~10 s) so a stuck guard never freezes the rows for good
      const t = tail;
      await t;
      await new Promise((r) => setTimeout(r, 250));
      let guard = false;
      try {
        const q = await navigator.locks.query();
        guard = [...q.held, ...q.pending].some((l) => l.name === 'ib2-kit-guard');
      } catch (e) { /* no Web Locks: the guard runs runCream at once, the tail check covers it */ }
      if (t === tail && !guard) return;
    }
  }
  // R13: while the cart settles (queue + kit guard) the badge keeps its last settled value and paints the newest count
  // once, at idle: a kit add no longer flashes its in-between counts. Writers: header swaps (render) and ib:cart:count.
  const badges = () => document.querySelectorAll('[data-ib-cart-count]');
  const paint = (s) => badges().forEach((el) => {
    el.textContent = s[0];
    el.style.display = s[1];
  });
  let held = null; // [text, display] kept on screen
  let latest = null; // newest count seen meanwhile
  function hold() {
    if (held) return;
    const el = badges()[0];
    if (!el) return;
    held = [el.textContent, el.style.display];
    settled().then(() => { // never awaited inside a slot: settled() waits for the queue tail
      const s = latest;
      held = null;
      latest = null;
      if (s) paint(s);
    });
  }
  function badge(s) {
    hold();
    if (!held) return paint(s);
    latest = s;
    paint(held);
  }
  // a drawer write (row qty/remove, recs Add) holds the list busy until the cart is idle again.
  // ponytail: 15 s ceiling, so a hung request never locks the rows and the checkout for good.
  function hush(write) {
    busy += 1;
    mark();
    return Promise.race([quiet(write).then(settled), new Promise((r) => setTimeout(r, 15000))]).finally(() => {
      busy -= 1;
      if (!busy) loading.clear();
      mark();
    });
  }
  const lineOp = (line, quantity) => {
    const key = line.dataset.ibLineKey;
    loading.add(key);
    return hush(setLine({ key, quantity }));
  };

  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    let el;
    if ((el = t.closest('[data-ib-cart-open]')) || ((el = t.closest('a[href]')) && cartLink(el))) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;
      e.preventDefault();
      open(); // opens at once; the refresh swaps the content only if the server cart differs from the one on screen
      refresh();
      return;
    }
    if (t.closest('[data-ib-cart-close]') || t.matches('[data-ib-cart-overlay]')) {
      close();
      return;
    }
    if ((el = t.closest('[data-ib-qty]'))) {
      const line = el.closest('[data-ib-line-key]');
      const input = line && line.querySelector('[data-ib-qty-input]');
      if (!input || el.disabled || busy) return;
      lineOp(line, Math.max(0, (parseInt(input.value, 10) || 0) + Number(el.getAttribute('data-ib-qty'))));
      return;
    }
    if ((el = t.closest('[data-ib-remove]'))) {
      const line = el.closest('[data-ib-line-key]');
      if (line && !el.disabled && !busy) lineOp(line, 0);
      return;
    }
    if ((el = t.closest('[data-ib-rec-step]'))) {
      // local quantity of a recs card (1..99), sent with its Add button
      const card = el.closest('[data-ib-upsell]');
      const n = card && card.querySelector('[data-ib-rec-qty]');
      if (!n) return;
      const q = Math.min(99, Math.max(1, (parseInt(n.textContent, 10) || 1) + Number(el.getAttribute('data-ib-rec-step'))));
      n.textContent = String(q);
      card.querySelector('[data-ib-rec-step="-1"]').disabled = q <= 1;
      card.querySelector('[data-ib-rec-step="1"]').disabled = q >= 99;
      return;
    }
    if ((el = t.closest('[data-ib-upsell-add]'))) {
      e.preventDefault();
      if (el.disabled || el.getAttribute('aria-disabled') === 'true') return;
      const card = el.closest('[data-ib-upsell]');
      const n = card && card.querySelector('[data-ib-rec-qty]');
      const qty = Math.max(1, parseInt(n ? n.textContent : '', 10) || 1);
      el.disabled = true;
      el.classList.add('is-loading');
      // window.ibCart.add, read now: the kit guard wraps it, so a regular SKU leaves as its Singola row (and its gift bag)
      // in this one add.js. enqueue() bypassed the guard, which split the line afterwards (16/10: 5 requests, a duplicate
      // row and a wrong total on screen for ~2 s with the checkout live).
      // the card usually leaves with the render (in cart); if it stays, it comes back once the cart is idle
      hush(window.ibCart.add({ items: [{ id: Number(el.dataset.variantId), quantity: qty }] })).then(() => {
        el.classList.remove('is-loading');
        el.disabled = false;
      });
      return;
    }
    if (t.closest('[data-ib-cart-retry]')) {
      showError(false);
      if (window.ibOffers && window.ibOffers.planReconcile) quiet(runCream([]));
      else refresh();
      return;
    }
    if ((el = t.closest('[data-ib-checkout]')) && (el.getAttribute('aria-disabled') === 'true' || busy)) {
      e.preventDefault();
      return;
    }
    // /cart page (Wonder markup): cart-remove-button > a.wt-cart__item__remove
    if ((el = t.closest('[data-ib-role="cart-page"] cart-remove-button'))) {
      e.preventDefault();
      setLine({ line: Number(el.dataset.index), quantity: 0 });
    }
  });

  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (t.matches('[data-ib-qty-input]')) {
      const line = t.closest('[data-ib-line-key]');
      // busy: a typed value is relative to an in-between row too, so it is reverted like a tap is ignored (VERIFY-H R15)
      if (busy) { t.value = t.defaultValue; return; }
      if (line) lineOp(line, Math.max(0, parseInt(t.value, 10) || 0));
    } else if (t.matches('[data-ib-upsell-picker]')) {
      const opt = t.options[t.selectedIndex];
      const card = t.closest('[data-ib-upsell]');
      const add = card.querySelector('[data-ib-upsell-add]');
      add.dataset.variantId = opt.value;
      add.disabled = opt.disabled;
      card.querySelector('[data-ib-upsell-price]').textContent = opt.dataset.price;
      const cmp = card.querySelector('[data-ib-upsell-cmp]');
      cmp.textContent = opt.dataset.cmp;
      cmp.hidden = !opt.dataset.cmp;
      const save = card.querySelector('[data-ib-upsell-save]');
      if (save) {
        save.textContent = opt.dataset.save;
        save.hidden = !opt.dataset.save;
      }
      card.querySelector('[data-ib-upsell-picker-val]').textContent = opt.textContent.trim();
    } else if (t.matches('[data-ib-role="cart-page"] .js-counter-quantity')) {
      // quantity-counter (Wonder global.js) fires a bubbling change on its input
      setLine({ line: Number(t.dataset.index), quantity: Math.max(0, parseInt(t.value, 10) || 0) });
    }
  });

  // open drawer: Esc closes, Tab / Shift+Tab cycle inside the panel
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]';
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    const o = overlay();
    const panel = o && o.getAttribute('data-state') === 'open' && o.querySelector('[data-ib-cart-box="panel"]');
    if (!panel) return;
    const items = [...panel.querySelectorAll(FOCUSABLE)].filter((x) => x.getAttribute('tabindex') !== '-1' && x.getClientRects().length);
    const a = document.activeElement;
    let to = null;
    if (!items.length) to = panel;
    else if (!panel.contains(a)) to = e.shiftKey ? items[items.length - 1] : items[0];
    else if (e.shiftKey && (a === items[0] || a === panel)) to = items[items.length - 1];
    else if (!e.shiftKey && a === items[items.length - 1]) to = items[0];
    if (to) {
      e.preventDefault();
      to.focus();
    }
  });
  document.addEventListener('scroll', (e) => {
    if (e.target instanceof Element && e.target.matches('[data-ib-recs]')) recEnd(e.target);
  }, true);
  window.addEventListener('resize', recEnds, { passive: true });

  // the emitters (ib2-product, product-form, ib-pdp) fire right after their add. Through ibCart.add the roles already show
  // that add's sections: open now (the refresh GET only re-read the same cart, ~0.4 s). A foreign fetch write re-renders
  // through the refresh it started (from its own answer for a JSON add): wait for it, so the drawer never opens on the old cart.
  // Once open, the list is busy until the cart is idle: the kit guard's reconcile after the add may still repair rows
  // (Instant PDP cream 2+1 gift, serum + Leopard pair), and the checkout must not leave with that in-between cart.
  document.addEventListener('ib:cart:open', () => {
    const go = () => {
      open();
      hush(Promise.resolve());
    };
    if (synced === writes && !pending) go();
    else refresh().finally(go);
  });

  // a page restored from the back/forward cache, or a tab coming back, may show a cart changed meanwhile (another tab).
  // The list and the checkout wait for that refresh and the kit guard's reconcile it starts (16/10, R13 other tabs).
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) hush(refresh());
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') hush(refresh());
  });

  document.addEventListener('ib:cart:error', (e) => {
    if (!e.detail || e.detail.op !== 'sections') showError(true);
  });

  document.addEventListener('ib:cart:count', (e) => {
    const n = e.detail && e.detail.count;
    if (typeof n !== 'number') return;
    badge([n < 100 ? String(n) : '', n > 0 ? '' : 'none']);
  });

  window.ibCart = {
    enqueue,
    add: (input) => enqueue({ op: 'add', input }),
    change: (input) => enqueue({ op: 'change', input }),
    update_lines: (input) => enqueue({ op: 'update_lines', input }),
    update_attributes: (input) => enqueue({ op: 'update_attributes', input }),
    clear: () => enqueue({ op: 'clear', input: {} }),
    get: () => enqueue({ op: 'get' }),
    batch,
    transaction,
    runCream,
    open,
    close,
    refresh: () => refresh(),
    ready: Promise.resolve()
  };
  emit('ib:cart:ready', {});

  // every cart load runs the reconcile when the offer planner (L1, loaded later) is present
  const onLoad = () => {
    if (window.ibOffers && window.ibOffers.planReconcile) quiet(runCream([]));
    // arrival from /cart: open the drawer once
    let arrive = location.hash === '#cart';
    try {
      if (sessionStorage.getItem('ib-cart-open')) {
        sessionStorage.removeItem('ib-cart-open');
        arrive = true;
      }
    } catch (e) { /* no storage: #cart only */ }
    if (location.hash === '#cart') history.replaceState(history.state, '', location.pathname + location.search);
    if (arrive) open();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onLoad);
  else onLoad();
})();
